import { currencyFormatter } from '@/helpers/currencyFormatter';
import { Button } from '@palmetto/palmetto-components';

import {
  CedProgramNameLabels,
  CedProgramTypes,
  DirectPayProjectStatus,
  DirectPayProjectStatusLabel,
  UserPermissions,
} from 'types';

import { isPalmettoFinanceUser } from '../../auth/RequirePalmettoFinanceUser';
import { convertIsoToDateMed } from '../../Underwriting/CreditApplications';

import { useAccountDetailsContext } from './AccountDetailsContext';

interface UseDirectPayPropertiesParams {
  directPayDetails: any;
  directPaySubCompanyName: string;
  allowsAction: boolean;
}

export function useDirectPayProperties({
  directPayDetails,
  directPaySubCompanyName,
  allowsAction,
}: UseDirectPayPropertiesParams) {
  const { account, claims, userPermissions, isLightReachAdmin, isAccountEditEnabled, openModal } =
    useAccountDetailsContext();

  // While an opt-in/opt-out is processing in the worker, the project-instance fields (project id,
  // identifier, ship/program/parallel-payment details) still hold the PREVIOUS project's values —
  // the worker hasn't resolved the new project yet. Hide them so they aren't shown next to the
  // newly-selected vendor; the terminal SSE event refetches and they reappear with real values.
  const isProcessing = directPayDetails?.projectStatus === DirectPayProjectStatus.processing;

  return {
    heading: 'Direct Pay',
    values: [
      { label: 'Direct Pay Vendor', value: directPayDetails?.directPayMaterialVendorAlias?.toUpperCase() },
      ...(directPayDetails?.subCompanyId && !isProcessing
        ? [{ label: 'Direct Pay Sub-Company', value: directPaySubCompanyName || directPayDetails?.subCompanyId }]
        : []),
      { label: 'Direct Pay Enabled', value: directPayDetails?.directPayEnabled ? 'Yes' : 'No' },
      ...(directPayDetails?.directPayMaterialVendorAlias === 'ced' && !isProcessing
        ? [{ label: 'Greentech Project Id', value: directPayDetails?.projectId || '' }]
        : []),
      ...(!isProcessing ? [{ label: 'Project Identifier', value: directPayDetails?.projectIdentifier || '' }] : []),
      ...(directPayDetails?.projectStatus
        ? [
            {
              label: 'Project Status',
              value:
                DirectPayProjectStatusLabel[directPayDetails?.projectStatus as keyof typeof DirectPayProjectStatus],
            },
          ]
        : []),
      ...(directPayDetails?.directPayMaterialVendorAlias === 'ced' && !isProcessing
        ? [
            {
              label: 'Ship Confirmation Date',
              value: directPayDetails?.shipConfirmationDate
                ? convertIsoToDateMed(directPayDetails?.shipConfirmationDate, { zone: 'UTC' })
                : '',
            },
          ]
        : []),
      ...(isPalmettoFinanceUser(claims) && directPayDetails?.directPayMaterialVendorAlias === 'ced' && !isProcessing
        ? [
            {
              label: 'Greentech Program Name',
              value: directPayDetails?.programName
                ? CedProgramNameLabels[directPayDetails?.programName as CedProgramTypes]
                : '',
            },
            {
              label: 'Parallel Payment Amount',
              value: currencyFormatter.format(directPayDetails?.parallelPaymentAmount ?? 0),
            },
            { label: 'Parallel Payment Status', value: directPayDetails?.parallelPaymentStatus || '' },
          ]
        : []),
      ...((isLightReachAdmin || userPermissions.includes(UserPermissions.orgAccountingAdmin)) &&
      directPayDetails?.directPayEnabled
        ? [
            {
              label: '',
              value: (
                <Button
                  variant="tertiary"
                  tone="danger"
                  size="xs"
                  onClick={() => openModal('directPayOptOut')}
                  isLoading={directPayDetails?.projectStatus === DirectPayProjectStatus.processing}
                  disabled={
                    // Ship-confirmation / finalization-invoice gates now live in the opt-out modal,
                    // which warns and disables Confirm (and offers override to permitted users). Keeping
                    // the button enabled lets it open the modal; the backend remains the real guard.
                    !allowsAction || directPayDetails?.projectStatus === DirectPayProjectStatus.processing
                  }
                >
                  Opt Out
                </Button>
              ),
            },
          ]
        : []),
      ...((isLightReachAdmin || userPermissions.includes(UserPermissions.orgAccountingAdmin)) &&
      !directPayDetails?.directPayEnabled
        ? [
            {
              label: '',
              value: (
                <Button
                  variant="tertiary"
                  tone="primary"
                  size="xs"
                  onClick={() => openModal('directPayOptIn')}
                  isLoading={directPayDetails?.projectStatus === DirectPayProjectStatus.processing}
                  disabled={
                    account?.cancellation?.isCancelled ||
                    !isAccountEditEnabled ||
                    directPayDetails?.projectStatus === DirectPayProjectStatus.processing
                  }
                >
                  Opt In
                </Button>
              ),
            },
          ]
        : []),
    ],
  };
}
